import { useRef, useCallback } from "react";
import { motion } from "framer-motion";
import useMotionPreferences from "@/hooks/useMotionPreferences";
import { DURATION, EASING } from "@/lib/motionSystem";

/**
 * SpotlightCard — Card with cursor-following spotlight glow.
 * Inspired by React Bits SpotlightCard component.
 */
export default function SpotlightCard({
  children,
  className = "",
  spotlightColor = "rgba(99, 102, 241, 0.15)",
  delay = 0,
  ...props
}) {
  const cardRef = useRef(null);
  const spotlightRef = useRef(null);
  const { allowDecorativeEffects } = useMotionPreferences();

  const handleMouseMove = useCallback((e) => {
    if (!allowDecorativeEffects) return;
    if (!cardRef.current || !spotlightRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    spotlightRef.current.style.background = `radial-gradient(400px circle at ${x}px ${y}px, ${spotlightColor}, transparent 60%)`;
    spotlightRef.current.style.opacity = "1";
  }, [allowDecorativeEffects, spotlightColor]);

  const handleMouseLeave = useCallback(() => {
    if (spotlightRef.current) {
      spotlightRef.current.style.opacity = "0";
    }
  }, []);

  return (
    <motion.div
      ref={cardRef}
      className={`
        relative overflow-hidden
        bg-[#0f172a]/60
        border border-[rgba(99,102,241,0.12)] rounded-2xl
        shadow-[0_8px_32px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.05)]
        [backdrop-filter:blur(20px)_saturate(180%)]
        [-webkit-backdrop-filter:blur(20px)_saturate(180%)]
        hover:-translate-y-[3px] hover:border-[rgba(99,102,241,0.3)]
        hover:shadow-[0_0_25px_rgba(99,102,241,0.12),0_8px_32px_rgba(0,0,0,0.4)]
        transition-all duration-200 ease-out
        will-change-transform
        ${className}
      `}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, y: 25 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: DURATION.sectionReveal, delay, ease: EASING.smooth }}
      {...props}
    >
      <div
        ref={spotlightRef}
        className="absolute inset-0 opacity-0 transition-opacity duration-300 pointer-events-none z-0 rounded-2xl"
      />
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
}
